import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Dashboard from "./Dashboard";
import imageCompression from "browser-image-compression";
import { useDispatch, useSelector } from "react-redux";
import {
  getMultimediaUploadUrl,
  submitMultimediaResponse,
  getScouts,
} from "../../actions/surveyActions";

const RequestPhoto = ({ location }) => {
  const { request } = location.state || {};
  const [active, setActive] = useState(false);
  const [images, setImages] = useState([]);
  const [preview, setPreview] = useState([]);
  const [photoIndex, setCurrentPhotoIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const history = useHistory();
  const dispatch = useDispatch();

  const fetchScout = useSelector((state) => state.fetchScout);
  const { data: scoutData } = fetchScout;

  React.useEffect(() => {
    dispatch(getScouts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const MAX_IMAGES = 6;

  const handleChange = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) {
      setImages([]);
      setPreview([]);
      return;
    }

    if (files.length > MAX_IMAGES) {
      event.preventDefault();
      alert(`Cannot upload more than ${MAX_IMAGES} files at once.`);
      return;
    }

    if (images.length + files.length > MAX_IMAGES) {
      event.preventDefault();
      alert(`Maximum ${MAX_IMAGES} photos allowed.`);
      return;
    }

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    files.forEach((file) => {
      imageCompression(file, options)
        .then((compressedFile) => {
          const newImage = compressedFile;
          const src = URL.createObjectURL(file);

          newImage.id = Math.random().toString(36).slice(2);
          newImage.file = file;

          setImages((previous) => [...previous, newImage]);
          setPreview((previous) => [...previous, src]);
        })
        .catch((compressionError) => {
          console.error("Compression error:", compressionError.message);
        });
    });
  };

  const setClassClicked = (image, index) => {
    setCurrentPhotoIndex(index);
    setActive(image);
  };

  const deSelectImages = () => {
    if (photoIndex === null) {
      return;
    }

    setImages((previous) => previous.filter((_, i) => i !== photoIndex));
    setPreview((previous) => previous.filter((_, i) => i !== photoIndex));

    setCurrentPhotoIndex(null);
    setActive(false);
  };

  const uploadSingleImage = async (imageFile, mimeType) => {
    const mmCampaignRef = request?.mmCampaignRef || request?.requestRef;

    const uploadUrlPayload = await dispatch(
      getMultimediaUploadUrl({
        mmCampaignRef,
        mimeType,
      })
    ).unwrap();

    const { uploadUrl, finalUrl } = uploadUrlPayload;

    await fetch(uploadUrl, {
      method: "PUT",
      body: imageFile,
      headers: {
        "Content-Type": mimeType,
      },
    });

    return finalUrl;
  };

  const onSubmit = async () => {
    if (images.length === 0) {
      setError("Please select at least one photo.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const mmCampaignRef = request?.mmCampaignRef || request?.requestRef;

      const uploadPromises = images.map((image) => {
        const mimeType = image.type || "image/jpeg";

        return uploadSingleImage(image.file || image, mimeType);
      });

      const finalUrls = await Promise.all(uploadPromises);

      const submitPayload = await dispatch(
        submitMultimediaResponse({
          mmCampaignRef,
          responseURLs: finalUrls,
        })
      ).unwrap();

      if (submitPayload?.responseId) {
        history.push({
          pathname: "/request-submit-success",
          state: { request },
        });
      }
    } catch (submitError) {
      console.error("Submit error:", submitError);
      setError(
        submitError?.message || "Failed to submit photos. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestName = request?.name || request?.requestName || "Photo request";

  return (
    <div>
      <Dashboard />

      <div className="modal">
        <Link
          to="/dashboard"
          className="modal-bg w-inline-block"
          style={{ backdropFilter: "blur(16px)" }}
        />

        <div className="padding-6 background-color-3 radius-large box-shadow-large width-full max-width-xl">
          <div className="modal-section">
            <h5 className="text-color-4 margin-0 weight-normal">
              <strong>{requestName}</strong>
            </h5>

            <div className="padding-top-6 margin-top-6 border-top-2px">
              <div
                className="upload recording"
                role="img"
                style={{
                  backgroundImage: `url(${
                    active === false ? preview[0] : active
                  })`,
                  objectFit: "contain",
                }}
              />

              <div className="media-actions margin-top-6">
                <button
                  type="button"
                  onClick={deSelectImages}
                  className="button background-color-brand absolute-bottom-right margin-6 background-color-red box-shadow-medium w-button"
                >
                  Delete
                </button>

                <label
                  htmlFor="photo-upload"
                  className="button background-color-brand absolute-bottom-right margin-6 background-color-white box-shadow-medium w-button"
                >
                  Upload
                  <input
                    name=""
                    type="file"
                    id="photo-upload"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleChange}
                  />
                </label>
              </div>

              <div className="grid-3-columns margin-top-6">
                {preview.map((imageSrc, index) => (
                  <img
                    key={images[index]?.id || index}
                    src={imageSrc}
                    alt={`Photo ${index + 1}`}
                    onClick={() => setClassClicked(imageSrc, index)}
                    style={{
                      height: "168px",
                      objectFit: "contain",
                      width: "168px",
                    }}
                    className={`thumbnail radius-large w-inline-block${
                      active === imageSrc ? " active" : ""
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="padding-top-6 border-top-2px flex-space-between margin-top-6 flex-align-baseline portrait-flex-column">
              <p className="text-color-2 margin-top-4">
                Submit 1–6 photos. Preview before sending.
                <br />
                <span style={{ color: "#FFCC00" }}>{error}</span>
              </p>

              <button
                type="button"
                className="button-secondary w-button"
                onClick={onSubmit}
                disabled={isSubmitting || images.length === 0}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestPhoto;
